import { useState, useRef } from 'react';
import { useZoneStore } from '../../store/zoneStore';
import { geoImportService } from '../../services/geoImportService';
import type { NetworkZone } from '../../types/zone';

interface CreateZoneDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (zone: NetworkZone) => void;
}

export const CreateZoneDialog = ({ isOpen, onClose, onCreated }: CreateZoneDialogProps) => {
  const { createZone, selectZone } = useZoneStore();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [networkFile, setNetworkFile] = useState<File | null>(null);
  const networkFileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    // Generate ID from name (lowercase, replace spaces with underscores)
    const id = name.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');

    if (!id) {
      setError('Name muss mindestens einen Buchstaben oder Zahl enthalten');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setStatusMessage('Zone wird erstellt...');

    try {
      // Step 1: Create the zone
      const newZone = await createZone({
        id,
        name: name.trim()
      });

      // Step 2: If network file was selected, select zone and import network
      // The backend will automatically calculate the zone polygon from the imported elements
      if (networkFile) {
        setStatusMessage('Netzwerk wird importiert...');
        try {
          // Select the new zone so geoImportService can use zone-scoped API
          selectZone(newZone.id);

          // Import network using shared service (same as sidebar button)
          await geoImportService.importUnifiedNetwork(networkFile);
        } catch (networkError) {
          console.warn('[CreateZoneDialog] Failed to import network:', networkError);
          // Don't fail zone creation if network import fails, but show warning
          setError('Zone erstellt, aber Netzwerk-Import fehlgeschlagen: ' +
            (networkError instanceof Error ? networkError.message : 'Unbekannter Fehler'));
        }
      }

      // Reset form
      setName('');
      setNetworkFile(null);
      setStatusMessage(null);
      if (networkFileInputRef.current) {
        networkFileInputRef.current.value = '';
      }

      onCreated(newZone);
    } catch (err) {
      console.error('[CreateZoneDialog] Failed to create zone:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Erstellen der Zone');
      setStatusMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNetworkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setNetworkFile(file || null);

    if (file && !name.trim()) {
      const baseName = file.name.replace(/\.(json|geojson)$/i, '');
      const transliterated = baseName
        .replace(/Ä/g, 'AE').replace(/Ö/g, 'OE').replace(/Ü/g, 'UE')
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss');
      const cleaned = transliterated.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (cleaned) setName(cleaned);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-primary-600">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-primary-100">
            Neue Zone erstellen
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-600 dark:text-error-400 text-sm">
              {error}
            </div>
          )}

          {statusMessage && (
            <div className="p-3 bg-info-500/10 border border-info-500/30 rounded-lg text-info-600 dark:text-info-400 text-sm flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {statusMessage}
            </div>
          )}

          <div>
            <label
              htmlFor="zone-name"
              className="block text-sm font-medium text-gray-700 dark:text-primary-200 mb-1"
            >
              Name *
            </label>
            <input
              id="zone-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Altstadt, Neustadt"
              className="w-full px-3 py-2 border border-gray-300 dark:border-primary-500 rounded-lg bg-white dark:bg-primary-700 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-2 focus:ring-accent-primary"
              disabled={isSubmitting}
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-primary-400">
              Die Zone-ID wird automatisch generiert (z.B. "altstadt")
            </p>
          </div>

          <div>
            <label
              htmlFor="zone-network"
              className="block text-sm font-medium text-gray-700 dark:text-primary-200 mb-1"
            >
              Netzwerk importieren (GeoJSON)
            </label>
            <input
              ref={networkFileInputRef}
              id="zone-network"
              type="file"
              accept=".json,.geojson"
              onChange={handleNetworkFileChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-primary-500 rounded-lg bg-white dark:bg-primary-700 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-2 focus:ring-accent-primary file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-accent-primary file:text-white hover:file:bg-accent-primary-hover file:cursor-pointer"
              disabled={isSubmitting}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-primary-400">
              Optional: Netz-GeoJSON mit Trafo, Hausanschluss, KVS, Kabel
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-primary-600 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-primary-200 bg-gray-100 dark:bg-primary-700 hover:bg-gray-200 dark:hover:bg-primary-600 rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-accent-primary disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary-hover rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-accent-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Erstelle...</span>
              </>
            ) : (
              <span>Zone erstellen</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
